import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {DownloadCallbackResponse} from '@Libraries/poster-download-library';
import type {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';

export enum DownloadStateTypes {
  DOWNLOADING = 'downloading',
  READY = 'ready',
  ERROR = 'error',
}

interface OrderDownloadState {
  state: DownloadStateTypes;
  downloadResult?: DownloadCallbackResponse;
  downloadURL?: string;
  printShopLink?: string;
  displayProgress?: number;
  downloadProgress?: number;
  name: string;
  fileType: FileTypeOptions;
}

interface State {
  orderStateHashmap: Record<string, OrderDownloadState>;
  currentProcessingOrderId?: string;
  webDownloadPosterTypeNames?: string[];
  upsellingRecommendationNumber: number | null;
  showLoadingPopup?: boolean;
  isFileDropdownOpen: boolean;
}

const initialState: State = {
  orderStateHashmap: {},
  currentProcessingOrderId: undefined,
  webDownloadPosterTypeNames: [],
  upsellingRecommendationNumber: null,
  showLoadingPopup: true,
  isFileDropdownOpen: false,
};

interface UpdateOrderStateActionProps {
  orderId: string;
  state?: DownloadStateTypes;
  downloadResult?: DownloadCallbackResponse;
  downloadURL?: string;
  printShopLink?: string;
  displayProgress?: number;
  downloadProgress?: number;
  name?: string;
  fileType?: FileTypeOptions;
}

export const downloadPanelSlice = createSlice({
  name: 'downloadDialog',
  initialState,
  reducers: {
    updateCurrentProcessingOrderId: (state, action: PayloadAction<string | undefined>) => {
      state.currentProcessingOrderId = action.payload;
    },
    updateOrderState: (state, action: PayloadAction<UpdateOrderStateActionProps>) => {
      state.orderStateHashmap[action.payload.orderId] = {
        ...state.orderStateHashmap[action.payload.orderId],
        ...action.payload,
      };
    },
    updateDisplayProgress: (state, action: PayloadAction<{orderId: string; displayProgress: number}>) => {
      const {orderId, displayProgress} = action.payload;
      if (state.orderStateHashmap[orderId]) {
        state.orderStateHashmap[orderId].displayProgress = displayProgress;
      }
    },
    clearOrderState: (state) => {
      state.orderStateHashmap = {};
      state.currentProcessingOrderId = undefined;
    },
    updateWebDownloadPosterTypeNames: (state, action: PayloadAction<string[]>) => {
      state.webDownloadPosterTypeNames = action.payload;
    },
    setUpsellingRecommendationNumberIfNeeded: (state, action: PayloadAction<number | null>) => {
      if (action.payload === null) {
        state.upsellingRecommendationNumber = null;
        return;
      }
      state.upsellingRecommendationNumber ??= action.payload;
    },
    updateDownloadLoadingPopupOpenState: (state, action: PayloadAction<boolean>) => {
      state.showLoadingPopup = action.payload;
    },
    toggleFileDropdownOpenState: (state, action: PayloadAction<boolean>) => {
      state.isFileDropdownOpen = action.payload;
    },
  },
});

export const {
  updateCurrentProcessingOrderId,
  updateDisplayProgress,
  setUpsellingRecommendationNumberIfNeeded,
  clearOrderState,
  updateOrderState,
  updateWebDownloadPosterTypeNames,
  updateDownloadLoadingPopupOpenState,
  toggleFileDropdownOpenState,
} = downloadPanelSlice.actions;
export const downloadReducer = downloadPanelSlice.reducer;
